import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { loadRemoteContainer } from '../../utils/loadRemoteContainer';

export const RUNNER_ENTRY = 'workflow-runner';
const RUNNER_CONTAINER = 'workflow_runner';

export const hasMountableRunner = (manifest) =>
  Boolean(manifest?.widgets?.[RUNNER_ENTRY]?.url);

// Mounts Workflow Engine's federated workflow-runner from a widget manifest
const WorkflowWidget = ({
  widgetManifest,
  bffBaseUrl,
  workflow,
  prefill,
  variant,
  onExit,
  onComplete,
  onError,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let unmountFn = null;
    let cancelled = false;

    // Render the generic alert and hand the raw error to the caller (telemetry
    // + fallback live there — see WalletEmails).
    const fail = (err) => {
      if (cancelled) return;
      setFailed(true);
      onError?.(err);
    };

    if (!hasMountableRunner(widgetManifest)) {
      fail(new Error('workflow-runner missing from manifest'));
      return undefined;
    }
    const runnerEntry = widgetManifest.widgets[RUNNER_ENTRY];

    // Not a security boundary: account-api's sanitize_manifest already dropped
    // any widget not on the allowlisted origin, so absolute URLs pass through
    // and the bffBaseUrl prefix only ever resolves a leftover relative path.
    const resolveAgainstBff = (url) =>
      /^https?:\/\//.test(url) ? url : `${bffBaseUrl ?? ''}${url}`;

    const screenWidgetsWithUrl = Object.entries(widgetManifest.widgets).filter(
      ([name, entry]) => name !== RUNNER_ENTRY && entry?.url,
    );
    const screenProvidingWidgets = Object.fromEntries(
      screenWidgetsWithUrl.map(([name, entry]) => [
        name,
        resolveAgainstBff(entry.url),
      ]),
    );

    loadRemoteContainer(
      resolveAgainstBff(runnerEntry.url),
      RUNNER_CONTAINER,
      './mount',
    )
      .then((mod) => {
        if (cancelled || !containerRef.current) return;
        const node = containerRef.current;
        // Before mount(), not after: a synchronous onError must still find a teardown.
        unmountFn = () => mod.unmount(node);
        mod.mount(node, {
          apiBaseUrl: axios.defaults.baseURL,
          csrfBaseUrl: axios.defaults.baseURL.replace(/\/api\/?$/, ''),
          widgets: screenProvidingWidgets,
          workflow,
          prefill,
          variant,
          onExit,
          onComplete,
          onError: (err) => fail(err),
        });
      })
      .catch((err) => {
        fail(err);
      });

    return () => {
      cancelled = true;
      unmountFn?.();
    };
    // Mount once: callers give each distinct widget its own identity (see the
    // key in WalletMergeWidget), so a new run remounts rather than updating
    // this instance's props in place — the inputs are stable for its lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (failed) {
    return <p role="alert">{t('workflowWidget.loadError')}</p>;
  }

  return <div ref={containerRef} />;
};

WorkflowWidget.propTypes = {
  widgetManifest: PropTypes.shape({
    widgets: PropTypes.object,
  }).isRequired,
  bffBaseUrl: PropTypes.string,
  workflow: PropTypes.string.isRequired,
  prefill: PropTypes.object,
  variant: PropTypes.string,
  onExit: PropTypes.func,
  onComplete: PropTypes.func,
  onError: PropTypes.func,
};

WorkflowWidget.defaultProps = {
  bffBaseUrl: undefined,
  prefill: undefined,
  variant: 'embedded',
  onExit: undefined,
  onComplete: undefined,
  onError: undefined,
};

export default WorkflowWidget;
